import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  ClipboardList,
  Clock,
  ChevronDown,
  ChevronUp,
  Package,
  Users,
  TrendingUp,
  Monitor,
  RefreshCw,
} from 'lucide-react';
import productionService from '../../services/productionService';
import inventoryService, { PendingApprovalLot, LotsSummary, ByproductSummaryItem } from '../../services/inventoryService';
import workScheduleService, { ScheduledWorkforceResponse } from '../../services/workScheduleService';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useDashboardWebSocket } from '../../hooks/useDashboardWebSocket';
import { wsService } from '../../services/websocket';
import IncompleteSessionsModal from '../../components/IncompleteSessionsModal';
import KpiDetailModal, { KpiDetailType } from '../../components/KpiDetailModal';
import StationOverview from '../../components/StationOverview';
import SessionOverview from '../../components/SessionOverview';
import ManagerApprovalQueue, { ManagerApprovalQueueHandle } from '../Supervisor/ManagerApprovalQueue';

interface SessionStats {
  planned: number;
  active: number;
  completed: number;
  total: number;
}

const Dashboard: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('manager.dashboard'));
  const queryClient = useQueryClient();

  // Real-time WebSocket updates for dashboard data
  useDashboardWebSocket();
  const [isIncompleteSessionsModalOpen, setIsIncompleteSessionsModalOpen] = useState(false);
  const [isApprovalQueueOpen, setIsApprovalQueueOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'station' | 'session'>('station');
  const [activeKpiDetail, setActiveKpiDetail] = useState<KpiDetailType>(null);
  const approvalQueueRef = useRef<ManagerApprovalQueueHandle>(null);

  // Fetch production sessions
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery(
    ['productionSessions', 'withSubSessions'],
    () => productionService.getProductionSessions({ include: 'sub_sessions' }),
    {
      staleTime: 30000,
    }
  );

  // Fetch pending approvals
  const { data: pendingApprovals = [] } = useQuery<PendingApprovalLot[]>(
    ['pending-approvals'],
    () => inventoryService.getPendingApprovals(),
    {
      refetchInterval: 60000, // Refresh every minute
      staleTime: 30000,
    }
  );

  // Fetch lot inventory summary (remaining material lots)
  const { data: lotsSummary } = useQuery<LotsSummary>(
    ['lots-summary'],
    () => inventoryService.getLotsSummary(),
    {
      staleTime: 60000,
    }
  );

  // Fetch today's workforce summary (scheduled staff + live assignment state)
  const today = new Date().toLocaleDateString('en-CA');
  const { data: workforce } = useQuery<ScheduledWorkforceResponse>(
    ['scheduled-workforce', today],
    () => workScheduleService.getScheduledWorkforce(today),
    {
      staleTime: 30000,
      refetchOnMount: 'always',
    }
  );

  // Fetch individual lots on demand (only when lots modal is open)
  // Uses status='remaining' so the popup matches the KPI summary card:
  // every lot with status != 'consumed' AND current_amount > 0.
  const { data: lotsData, isLoading: lotsLoading } = useQuery(
    ['kpi-lots-detail'],
    () => inventoryService.getLots({ status: 'remaining', limit: 250 }),
    {
      enabled: activeKpiDetail === 'lots',
      staleTime: 60000,
    }
  );

  // Fetch byproduct inventory summary (only when lots modal is open)
  const { data: byproducts = [], isLoading: byproductsLoading } = useQuery<ByproductSummaryItem[]>(
    ['kpi-byproduct-summary'],
    () => inventoryService.getByproductSummary(),
    {
      enabled: activeKpiDetail === 'lots',
      staleTime: 60000,
    }
  );

  // Auto-open/close approval queue based on pending items
  useEffect(() => {
    setIsApprovalQueueOpen(pendingApprovals.length > 0);
  }, [pendingApprovals.length]);

  // WebSocket for real-time updates
  useEffect(() => {
    wsService.connect();

    const handleSessionUpdated = () => {
      queryClient.invalidateQueries(['productionSessions']);
      queryClient.invalidateQueries(['scheduled-workforce']);
    };

    const handleScheduleUpdated = () => {
      queryClient.invalidateQueries(['scheduled-workforce']);
    };

    wsService.on('production-session-updated', handleSessionUpdated);
    wsService.on('production-session-created', handleSessionUpdated);
    wsService.on('station-updated', handleSessionUpdated);
    wsService.on('schedule-updated', handleScheduleUpdated);

    return () => {
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('production-session-created', handleSessionUpdated);
      wsService.off('station-updated', handleSessionUpdated);
      wsService.off('schedule-updated', handleScheduleUpdated);
    };
  }, [queryClient]);

  // Calculate session stats
  const sessionStats: SessionStats = React.useMemo(() => {
    // Use local timezone for today's date comparison
    const today = new Date().toLocaleDateString('en-CA');
    const todaySessions = sessions.filter((s: any) =>
      s.scheduled_date?.split('T')[0] === today && s.status !== 'cancelled'
    );

    const planned = todaySessions.filter((s: any) =>
      ['pending', 'submitted', 'received', 'ready'].includes(s.status)
    ).length;
    const active = todaySessions.filter((s: any) =>
      ['active', 'in_progress', 'paused'].includes(s.status)
    ).length;
    const completed = todaySessions.filter((s: any) =>
      s.status === 'completed'
    ).length;

    return {
      planned,
      active,
      completed,
      total: planned + active + completed,
    };
  }, [sessions]);

  // Calculate incomplete sessions from previous days
  const incompleteSessions = React.useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    return sessions.filter((session: any) => {
      const scheduledDate = session.scheduled_date?.split('T')[0];
      const isBeforeToday = scheduledDate && scheduledDate < today;
      const isIncomplete = !['completed', 'cancelled'].includes(session.status);
      return isBeforeToday && isIncomplete;
    });
  }, [sessions]);

  const isLoading = sessionsLoading;

  // Sessions shown in the session KPI modal:
  // all of today's sessions, including cancelled, for manager context
  const sessionDetailSessions = React.useMemo(() => {
    const todayStr = new Date().toLocaleDateString('en-CA');
    return sessions.filter((s: any) => s.scheduled_date?.split('T')[0] === todayStr);
  }, [sessions]);

  // Filter sessions for the production progress overview:
  // Today's sessions + any still-active sessions from previous days
  const todaySessions = React.useMemo(() => {
    const todayStr = new Date().toLocaleDateString('en-CA');
    return sessions.filter((s: any) => {
      if (s.status === 'cancelled') return false;
      const scheduledDate = s.scheduled_date?.split('T')[0];
      const isToday = scheduledDate === todayStr;
      const isPastAndStillRunning = scheduledDate < todayStr && ['active', 'paused'].includes(s.status);
      return isToday || isPastAndStillRunning;
    });
  }, [sessions]);

  // Calculate today's output totals across all sub-sessions (in cases)
  const todaysOutput = React.useMemo(() => {
    let totalOutput = 0;
    let totalExpected = 0;

    todaySessions.forEach((session: any) => {
      const subs = (session.sub_sessions || []).filter((sub: any) => sub.status !== 'cancelled');
      subs.forEach((sub: any) => {
        totalOutput += Number(sub.boxes_produced) || 0;
        totalExpected += Number(sub.expected_boxes) || 0;
      });
    });

    const completionPct = totalExpected > 0
      ? ((totalOutput / totalExpected) * 100).toFixed(2)
      : 0;

    return { totalOutput, totalExpected, completionPct };
  }, [todaySessions]);

  return (
    <div data-tour="manager-dashboard" className="flex flex-col gap-3 min-h-full">
      {/* Stats Cards - 2x2 on large screens, 4-col on xl */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {/* Today's Sessions */}
        <div
          onClick={() => setActiveKpiDetail('sessions')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-indigo-300 dark:hover:ring-indigo-600 transition-shadow"
          title={t('manager.kpiDetail.clickToViewDetails')}
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
                <ClipboardList className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('manager.todaysSessions')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {isLoading ? '-' : sessionStats.total}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-blue-200 dark:border-blue-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.plannedSessions')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-blue-600 dark:text-blue-400">{isLoading ? '-' : sessionStats.planned}</p>
            </div>
            <div className="text-center border border-green-200 dark:border-green-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.activeSessions')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-green-600 dark:text-green-400">{isLoading ? '-' : sessionStats.active}</p>
            </div>
            <div className="text-center border border-gray-200 dark:border-gray-700 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.completedSessions')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-gray-600 dark:text-gray-400">{isLoading ? '-' : sessionStats.completed}</p>
            </div>
          </div>
        </div>

        {/* Today's Workforce */}
        <div
          onClick={() => setActiveKpiDetail('workforce')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-cyan-300 dark:hover:ring-cyan-600 transition-shadow"
          title={t('manager.kpiDetail.clickToViewDetails')}
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-full">
                <Users className="w-4 h-4 2xl:w-5 2xl:h-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('manager.todaysWorkforce')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {workforce
                ? `${workforce.summary.available_operators + workforce.summary.available_packers}/${workforce.summary.total_operators + workforce.summary.total_packers}`
                : '-'}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-green-200 dark:border-green-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.operators')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-green-600 dark:text-green-400">
                {workforce ? `${workforce.summary.available_operators}/${workforce.summary.total_operators}` : '-'}
              </p>
            </div>
            <div className="text-center border border-blue-200 dark:border-blue-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.packers')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-blue-600 dark:text-blue-400">
                {workforce ? `${workforce.summary.available_packers}/${workforce.summary.total_packers}` : '-'}
              </p>
            </div>
            <div className="text-center border border-purple-200 dark:border-purple-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.stations')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-purple-600 dark:text-purple-400">{workforce?.summary?.total_stations ?? '-'}</p>
            </div>
          </div>
        </div>

        {/* Remaining Material Lots */}
        <div
          onClick={() => setActiveKpiDetail('lots')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-orange-300 dark:hover:ring-orange-600 transition-shadow"
          title={t('manager.kpiDetail.clickToViewDetails')}
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                <Package className="w-4 h-4 2xl:w-5 2xl:h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('manager.remainingMaterialLots')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {lotsSummary ? lotsSummary.available_lots : '-'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-orange-200 dark:border-orange-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.lots')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-orange-600 dark:text-orange-400">{lotsSummary ? lotsSummary.available_lots : '-'}</p>
            </div>
            <div className="text-center border border-orange-200 dark:border-orange-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.weight')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-orange-600 dark:text-orange-400">{lotsSummary ? Number(lotsSummary.total_available_weight).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}<span className="text-xs 2xl:text-sm font-normal text-gray-400 ml-1">lbs</span></p>
            </div>
          </div>
        </div>

        {/* Today's Output */}
        <div
          onClick={() => setActiveKpiDetail('output')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-emerald-300 dark:hover:ring-emerald-600 transition-shadow"
          title={t('manager.kpiDetail.clickToViewDetails')}
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                <TrendingUp className="w-4 h-4 2xl:w-5 2xl:h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('manager.todaysOutput')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {isLoading ? '-' : todaysOutput.totalExpected > 0 ? `${todaysOutput.completionPct}%` : '-'}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-emerald-200 dark:border-emerald-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.output')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-emerald-600 dark:text-emerald-400">{isLoading ? '-' : todaysOutput.totalOutput.toLocaleString(undefined, { maximumFractionDigits: 0 })}<span className="text-xs 2xl:text-sm font-normal text-gray-400 ml-1">CS</span></p>
            </div>
            <div className="text-center border border-orange-200 dark:border-orange-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.expected')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-orange-600 dark:text-orange-400">{isLoading ? '-' : todaysOutput.totalExpected.toLocaleString(undefined, { maximumFractionDigits: 0 })}<span className="text-xs 2xl:text-sm font-normal text-gray-400 ml-1">CS</span></p>
            </div>
            <div className="text-center border border-blue-200 dark:border-blue-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.completion')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-blue-600 dark:text-blue-400">{isLoading ? '-' : todaysOutput.totalExpected > 0 ? `${todaysOutput.completionPct}%` : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Approval Queue Section */}
      <div data-tour="approval-cards" className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setIsApprovalQueueOpen(prev => !prev)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('managerApproval.title')}
            </h2>
            {pendingApprovals.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded-full">
                {pendingApprovals.length}
              </span>
            )}
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); approvalQueueRef.current?.refresh(); }}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <RefreshCw className={`w-4 h-4 ${approvalQueueRef.current?.loading ? 'animate-spin' : ''}`} />
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
              {t('managerApproval.subtitle')}
            </span>
            {isApprovalQueueOpen
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </div>
        </button>
        {isApprovalQueueOpen && <ManagerApprovalQueue ref={approvalQueueRef} />}
      </div>

      {/* Production Progress — Station / Session toggle */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 xl:p-5 flex-1 flex flex-col">
        {/* Toggle buttons — rendered above the active view's own header */}
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setViewMode('station')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              viewMode === 'station'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            <Monitor className="w-4 h-4" />
            {t('manager.stationView')}
          </button>
          <button
            onClick={() => setViewMode('session')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              viewMode === 'session'
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            {t('manager.sessionView')}
          </button>
        </div>

        {viewMode === 'station' ? (
          <StationOverview />
        ) : (
          <SessionOverview sessions={sessions} sessionsLoading={sessionsLoading} />
        )}
      </div>

      {/* Incomplete Sessions Modal */}
      <IncompleteSessionsModal
        isOpen={isIncompleteSessionsModalOpen}
        onClose={() => setIsIncompleteSessionsModalOpen(false)}
        sessions={incompleteSessions}
      />

      {/* KPI Detail Modal — opens when any KPI card is clicked */}
      <KpiDetailModal
        type={activeKpiDetail}
        onClose={() => setActiveKpiDetail(null)}
        todaySessions={sessionDetailSessions}
        workforce={workforce}
        lots={lotsData?.lots || []}
        lotsLoading={lotsLoading}
        byproducts={byproducts}
        byproductsLoading={byproductsLoading}
      />

    </div>
  );
};

export default Dashboard;
